import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { submitOrderRequest } from '../services/orderRequest'

export const useCartStore = defineStore('b2b-cart', () => {
  // State
  const items = ref([])
  const sessionId = ref(null)
  const customerInfo = ref({
    businessName: '',
    contactName: '', 
    email: '',
    phone: '',
    shippingAddress: {
      street1: '',
      street2: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'US'
    },
    billingAddress: {
      street1: '',
      street2: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'US'
    },
    sameAsBilling: true
  })
  const orderNotes = ref('')
  const preferredDeliveryDate = ref('')
  const isLoading = ref(false)
  const lastUpdated = ref(null)

  // Computed properties
  const itemCount = computed(() => {
    return items.value.reduce((total, item) => total + item.quantity, 0)
  })

  const uniqueItemCount = computed(() => {
    return items.value.length
  })

  const subtotal = computed(() => {
    return items.value.reduce((total, item) => {
      return total + (item.price * item.quantity)
    }, 0)
  })

  // Final tax and delivery are confirmed with the request, never fabricated.
  const estimatedTax = computed(() => null)
  const shippingCost = computed(() => null)
  const total = computed(() => subtotal.value)
  const hasMinimumOrder = computed(() => items.value.length > 0)

  const validationErrors = computed(() => {
    const errors = []
    
    if (items.value.length === 0) {
      errors.push('Cart is empty')
    }
    
    
    // Check MOQ for each item
    items.value.forEach(item => {
      if (item.quantity < item.minimumOrderQuantity) {
        errors.push(`${item.name} requires minimum quantity of ${item.minimumOrderQuantity}`)
      }
    })
    
    return errors
  })

  const canCheckout = computed(() => {
    return validationErrors.value.length === 0
  })

  // Actions
  const generateSessionId = () => {
    if (!sessionId.value) {
      sessionId.value = 'b2b-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9)
    }
    return sessionId.value
  }

  const addItem = (product) => {
    const existingItem = items.value.find(item => item.id === product.id)
    
    if (existingItem) {
      // Update quantity but ensure it meets MOQ
      const newQuantity = existingItem.quantity + product.quantity
      existingItem.quantity = Math.max(newQuantity, product.minimumOrderQuantity)
    } else {
      // Add new item with MOQ validation
      const itemToAdd = {
        id: product.id,
        name: product.name,
        sku: product.sku,
        price: product.price,
        quantity: Math.max(product.quantity, product.minimumOrderQuantity),
        minimumOrderQuantity: product.minimumOrderQuantity,
        packageInfo: product.packageInfo,
        leadTimeDays: product.leadTimeDays || 7,
        imageUrl: product.imageUrl || null,
        addedAt: new Date().toISOString()
      }
      items.value.push(itemToAdd)
    }
    
    lastUpdated.value = new Date().toISOString()
    saveToLocalStorage()
  }

  const updateItemQuantity = (itemId, quantity) => {
    const item = items.value.find(item => item.id === itemId)
    if (item) {
      // Ensure quantity meets MOQ
      item.quantity = Math.max(quantity, item.minimumOrderQuantity)
      lastUpdated.value = new Date().toISOString()
      saveToLocalStorage()
    }
  }

  const removeItem = (itemId) => {
    const index = items.value.findIndex(item => item.id === itemId)
    if (index > -1) {
      items.value.splice(index, 1)
      lastUpdated.value = new Date().toISOString()
      saveToLocalStorage()
    }
  }

  const clearCart = () => {
    items.value = []
    orderNotes.value = ''
    preferredDeliveryDate.value = ''
    lastUpdated.value = new Date().toISOString()
    saveToLocalStorage()
  }

  const updateCustomerInfo = (newCustomerInfo) => {
    customerInfo.value = { ...customerInfo.value, ...newCustomerInfo }
    saveToLocalStorage()
  }

  const updateOrderNotes = (notes) => {
    orderNotes.value = notes
    saveToLocalStorage()
  }

  const updatePreferredDeliveryDate = (date) => {
    preferredDeliveryDate.value = date
    saveToLocalStorage()
  }

  // Draft order functionality
  const saveDraftOrder = async (draftName) => {
    if (items.value.length === 0) {
      throw new Error('Cannot save empty cart as draft')
    }

    const draftOrder = {
      id: 'draft-' + Date.now(),
      name: draftName,
      items: [...items.value],
      customerInfo: { ...customerInfo.value },
      orderNotes: orderNotes.value,
      preferredDeliveryDate: preferredDeliveryDate.value,
      subtotal: subtotal.value,
      total: total.value,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }

    // In production, this would be an API call
    const existingDrafts = JSON.parse(localStorage.getItem('b2b-draft-orders') || '[]')
    existingDrafts.push(draftOrder)
    localStorage.setItem('b2b-draft-orders', JSON.stringify(existingDrafts))

    return draftOrder
  }

  const loadDraftOrder = (draftId) => {
    const existingDrafts = JSON.parse(localStorage.getItem('b2b-draft-orders') || '[]')
    const draft = existingDrafts.find(d => d.id === draftId)
    
    if (draft) {
      items.value = [...draft.items]
      customerInfo.value = { ...draft.customerInfo }
      orderNotes.value = draft.orderNotes
      preferredDeliveryDate.value = draft.preferredDeliveryDate
      lastUpdated.value = new Date().toISOString()
      saveToLocalStorage()
      return true
    }
    return false
  }

  // Template functionality  
  const saveAsTemplate = async (templateName) => {
    if (items.value.length === 0) {
      throw new Error('Cannot save empty cart as template')
    }

    const template = {
      id: 'template-' + Date.now(),
      name: templateName,
      items: items.value.map(item => ({
        id: item.id,
        name: item.name,
        sku: item.sku,
        price: item.price,
        quantity: item.quantity,
        minimumOrderQuantity: item.minimumOrderQuantity,
        packageInfo: item.packageInfo
      })),
      createdAt: new Date().toISOString()
    }

    // In production, this would be an API call
    const existingTemplates = JSON.parse(localStorage.getItem('b2b-order-templates') || '[]')
    existingTemplates.push(template)
    localStorage.setItem('b2b-order-templates', JSON.stringify(existingTemplates))

    return template
  }

  const loadFromTemplate = (templateId) => {
    const existingTemplates = JSON.parse(localStorage.getItem('b2b-order-templates') || '[]')
    const template = existingTemplates.find(t => t.id === templateId)
    
    if (template) {
      // Clear current cart and load template items
      items.value = template.items.map(item => ({
        ...item,
        addedAt: new Date().toISOString()
      }))
      lastUpdated.value = new Date().toISOString()
      saveToLocalStorage()
      return true
    }
    return false
  }

  // The public request is persisted; only staff confirmation can create a sale.
  const processCheckout = async () => {
    if (!items.value.length) throw new Error('Add products before requesting an order.')
    isLoading.value = true
    try {
      const info = customerInfo.value
      const address = info.sameAsBilling ? info.billingAddress : info.shippingAddress
      const receipt = await submitOrderRequest({
        businessName: info.businessName, contactName: info.contactName,
        email: info.email, phone: info.phone || undefined,
        addressLine1: [address.street1, address.street2].filter(Boolean).join(', ') || undefined,
        city: address.city || undefined, stateCode: address.state || undefined, postalCode: address.zipCode || undefined,
        audience: info.audience || 'trade',
        // The partner catalog counts packages, not cases; never relabel quantities.
        items: items.value.map(item => ({ productId: Number(item.id), quantity: Number(item.quantity), unit: 'package' })),
        requestedDeliveryDate: preferredDeliveryDate.value || undefined,
        notes: orderNotes.value || undefined, source: 'partners.banjinos.com'
      })
      clearCart()
      return { success: true, orderNumber: receipt.reference, message: 'Order request saved' }
    } finally { isLoading.value = false }
  }

  // Persistence
  const saveToLocalStorage = () => {
    const cartData = {
      items: items.value,
      sessionId: sessionId.value,
      customerInfo: customerInfo.value,
      orderNotes: orderNotes.value,
      preferredDeliveryDate: preferredDeliveryDate.value,
      lastUpdated: lastUpdated.value
    }
    localStorage.setItem('b2b-cart', JSON.stringify(cartData))
  }

  const loadFromLocalStorage = () => {
    const saved = localStorage.getItem('b2b-cart')
    if (saved) {
      try {
        const cartData = JSON.parse(saved)
        items.value = cartData.items || []
        sessionId.value = cartData.sessionId
        customerInfo.value = { ...customerInfo.value, ...(cartData.customerInfo || {}) }
        orderNotes.value = cartData.orderNotes || ''
        preferredDeliveryDate.value = cartData.preferredDeliveryDate || ''
        lastUpdated.value = cartData.lastUpdated
      } catch (error) {
        console.error('Failed to load cart from localStorage:', error)
      }
    }
  }

  // Quick reorder functionality
  const reorderFromHistory = async (orderNumber) => {
    // In production, this would fetch order details from API
    // For now, using mock functionality
    const mockOrderItems = [
      {
        id: 1,
        name: 'Western Spritz Original',
        sku: 'WS-001-24',
        price: 18.50,
        quantity: 24,
        minimumOrderQuantity: 12,
        packageInfo: '24 × 12oz'
      }
    ]
    
    // Clear current cart and add reorder items
    items.value = mockOrderItems.map(item => ({
      ...item,
      addedAt: new Date().toISOString()
    }))
    
    lastUpdated.value = new Date().toISOString()
    saveToLocalStorage()
    
    return true
  }

  // Initialize store
  loadFromLocalStorage()
  
  // Auto-save on changes
  watch([items, customerInfo, orderNotes, preferredDeliveryDate], () => {
    saveToLocalStorage()
  }, { deep: true })

  return {
    // State
    items,
    sessionId,
    customerInfo,
    orderNotes,
    preferredDeliveryDate,
    isLoading,
    lastUpdated,
    
    // Computed
    itemCount,
    uniqueItemCount,
    subtotal,
    estimatedTax,
    shippingCost,
    total,
    hasMinimumOrder,
    validationErrors,
    canCheckout,
    
    // Actions
    generateSessionId,
    addItem,
    updateItemQuantity,
    removeItem,
    clearCart,
    updateCustomerInfo,
    updateOrderNotes,
    updatePreferredDeliveryDate,
    saveDraftOrder,
    loadDraftOrder,
    saveAsTemplate,
    loadFromTemplate,
    processCheckout,
    reorderFromHistory,
    
    // Utilities
    saveToLocalStorage,
    loadFromLocalStorage
  }
})